import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Users, Zap, Smartphone, Nfc, Palette, Layers, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { CtaWhatsApp } from "../components/Cta";
import { Reveal, MaskedLines, Marquee, Tilt, EASE } from "../components/motion";
import NfcDemo from "../components/NfcDemo";
import SurMesure from "../components/SurMesure";
import { CardPair } from "../components/CardMockup";
import OfferCard from "../components/OfferCard";
import { OFFERS, SLOGAN, PRICE_TEASER, DELIVERY_DELAY, SITE_URL } from "../config/site";
import { FAQ_HOME } from "../data/faq";
import { METIERS } from "../data/metiers";import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const BENEFITS = [
  { icon: Star, t: "Plus d'avis Google", d: "Le client laisse un avis au moment exact où il est le plus satisfait." },
  { icon: Users, t: "Plus d'abonnés", d: "Instagram et TikTok à portée de geste : votre communauté grandit à chaque visite." },
  { icon: Zap, t: "Accès instantané", d: "Menu, téléphone, itinéraire, réseaux : vos infos pro en une seconde." },
  { icon: Smartphone, t: "Zéro application", d: "Rien à installer, ni pour vous ni pour vos clients. Tout s'ouvre dans le navigateur." },
  { icon: Nfc, t: "Puce NFC intégrée", d: "Un simple geste du smartphone suffit : votre univers digital s'ouvre instantanément, sur iPhone et Android." },
  { icon: Palette, t: "À votre image", d: "Logo, couleurs, identité visuelle : chaque carte est conçue pour votre marque." },
  { icon: Layers, t: "Une carte pour tout", d: "Une seule carte connecte l'ensemble de votre univers digital." },
];

const CHAPTERS = [
  { n: "01", t: "Personnalisez", d: "Design, logo, couleurs, informations : nous concevons ensemble une carte et une page digitale qui ressemblent à votre entreprise." },
  { n: "02", t: "Approchez", d: "Votre client approche simplement son smartphone de la carte — le NFC fait le reste. Aucune application, aucun réglage." },
  { n: "03", t: "Découvrez", d: "Votre page digitale s'ouvre : avis Google, réseaux sociaux, menu, contact… Tout votre univers, en un geste." },
];

export default function Home() {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Noveocard",
    url: SITE_URL,
  };

  return (
    <>
      <Seo
        title=""
        path="/"
        description="Cartes digitales professionnelles premium personnalisées (PVC, NFC). Plus d'avis Google, plus d'abonnés, zéro application. À partir de 40 € TTC. Réponse sous 30 minutes sur WhatsApp."
        jsonld={[websiteSchema]}
      />

      {/* ——— HERO ——— */}
      <section className="grain relative overflow-hidden bg-nuit-900 pb-20 pt-32 text-white sm:pt-40">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(55% 45% at 78% 18%, rgba(22,119,255,0.28) 0%, transparent 70%), radial-gradient(45% 40% at 8% 90%, rgba(22,119,255,0.14) 0%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="container-nv relative grid items-center gap-14 lg:grid-cols-[1.15fr_1fr]">
          <div>
            <motion.p
              className="eyebrow text-electric-300"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              Identité digitale premium
            </motion.p>
            <h1 className="mt-5 font-display text-5xl font-bold leading-[1.04] tracking-tight sm:text-6xl lg:text-[3.9rem]">
              <MaskedLines
                lines={["Votre identité.", <span className="text-electric-300">En version digitale.</span>]}
                delay={0.15}
              />
            </h1>
            <motion.p
              className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.9, ease: EASE }}
            >
              Une carte digitale premium personnalisée qui connecte votre entreprise à votre
              univers digital.
            </motion.p>
            <motion.div
              className="mt-9 flex flex-wrap items-center gap-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
            >
              <CtaWhatsApp size="lg" showResponseTime testid="hero-cta-whatsapp" />
              <Link
                to="/la-carte"
                data-testid="hero-discover-link"
                className="group inline-flex items-center gap-2 font-display text-base font-semibold text-white/85 transition-colors hover:text-white"
              >
                Découvrir la carte
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </motion.div>
            <motion.p
              className="mt-6 text-sm font-medium text-electric-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.9 }}
            >
              {PRICE_TEASER}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.45, duration: 1.1, ease: EASE }}
          >
            <NfcDemo />
          </motion.div>
        </div>
      </section>

      {/* ——— MARQUEE ——— */}
      <div className="border-y border-nuit-900/10 bg-white py-5">
        <Marquee
          items={METIERS.map((m) => m.name).concat(["Cafés & bars", "Coachs", "Indépendants"])}
          itemClassName="font-display text-sm font-semibold uppercase tracking-[0.25em] text-nuit-900/45"
        />
      </div>

      {/* ——— LA CARTE NOVEOCARD ——— */}
      <section className="grain relative overflow-hidden bg-nuit-950 py-20 text-white sm:py-28">
        <div className="container-nv grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow text-electric-300">La carte Noveocard</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-5xl">
              Voici la carte que vous recevez.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              Cadre foil métallisé, ornement à losange, symbole NFC et verso complet avec
              vos accès directs. Votre logo et vos couleurs — n'importe lesquelles — à la place
              des nôtres.
            </p>
            <p className="mt-5 max-w-xl rounded-2xl border border-electric/30 bg-electric/10 p-4 text-sm leading-relaxed text-electric-200">
              <strong className="text-white">Personnalisation sur-mesure incluse, validée avec vous
              avant production.</strong> Aucune carte n'est un simple logo sur une couleur.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <CtaWhatsApp testid="home-card-cta" />
              <Link
                to="/galerie"
                data-testid="home-card-gallery-link"
                className="link-line font-display font-semibold text-electric-300"
              >
                Voir la galerie
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative flex items-center gap-5" style={{ perspective: "1200px" }}>
              <div className="absolute -inset-12 rounded-full bg-electric/15 blur-3xl" aria-hidden="true" />
              <Tilt max={9} className="relative w-1/2 -rotate-3">
                <img
                  src="/assets/ref-recto.webp"
                  alt="Carte Noveocard recto : bleu nuit lumineux, logo N et signature Noveocard, slogan « Un simple geste ouvre un monde »"
                  width="1600"
                  height="1004"
                  className="w-full rounded-2xl shadow-[0_36px_70px_-28px_rgba(0,0,0,0.75)]"
                />
              </Tilt>
              <Tilt max={9} className="relative w-1/2 rotate-3 translate-y-5">
                <img
                  src="/assets/ref-verso.webp"
                  alt="Carte Noveocard verso : « Approchez votre téléphone », accès WhatsApp, site internet, avis Google, Instagram et TikTok"
                  loading="lazy"
                  width="1600"
                  height="1004"
                  className="w-full rounded-2xl shadow-[0_36px_70px_-28px_rgba(0,0,0,0.75)]"
                />
              </Tilt>
            </div>
            <p className="mt-10 text-center text-xs font-medium uppercase tracking-[0.2em] text-white/40">
              PVC 86 × 54 mm · NFC · finition premium recto & verso
            </p>
          </Reveal>
        </div>
      </section>

      {/* ——— BÉNÉFICES ——— */}
      <section className="bg-white py-20 sm:py-28">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Pourquoi Noveocard</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-5xl">
              Une carte. Tout votre univers digital.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
              La carte n'est pas le produit : c'est la porte d'entrée. Derrière, une page digitale
              qui travaille pour vous à chaque geste de vos clients.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.t} delay={(i % 3) * 0.08} className={i === BENEFITS.length - 1 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <article className="group h-full rounded-3xl border border-nuit-900/10 bg-brume p-7 transition-[transform,box-shadow,background-color] duration-500 hover:-translate-y-1.5 hover:bg-white hover:shadow-[0_24px_50px_-24px_rgba(7,26,45,0.3)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-nuit-900 text-electric-300 transition-colors duration-500 group-hover:bg-electric group-hover:text-white">
                    <b.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-nuit-900">{b.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-steel">{b.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ——— SUR-MESURE ——— */}
      <SurMesure />

      {/* ——— MANIFESTE / COMMENT ÇA MARCHE ——— */}
      <section className="grain relative overflow-hidden bg-nuit-950 py-20 text-white sm:py-28">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow text-electric-300">Comment ça marche</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-5xl">
              Trois gestes. Zéro friction.
            </h2>
          </Reveal>
          <div className="mt-16 space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={0.05}>
                <div className="grid items-center gap-6 border-t border-white/10 py-12 sm:grid-cols-[auto_1fr_auto] sm:gap-14">
                  <span className="font-display text-7xl font-bold leading-none text-white/10 sm:text-9xl" aria-hidden="true">
                    {c.n}
                  </span>
                  <div className="max-w-xl">
                    <h3 className="font-display text-2xl font-bold sm:text-4xl">{c.t}</h3>
                    <p className="mt-3 text-base leading-relaxed text-white/65 sm:text-lg">{c.d}</p>
                  </div>
                  {i === 1 && (
                    <div className="hidden w-72 lg:block">
                      <div className="animate-float-slow">
                        <CardPair name="Votre Établissement" />
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6">
            <Link
              to="/comment-ca-marche"
              data-testid="home-hiw-link"
              className="group inline-flex items-center gap-2 font-display text-lg font-semibold text-electric-300 transition-colors hover:text-electric-200"
            >
              Voir le fonctionnement en détail
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ——— OFFRES ——— */}
      <section className="bg-brume py-20 sm:py-28" id="offres">
        <div className="container-nv">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow">Nos offres</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-5xl">
              {PRICE_TEASER}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
              Des prix simples, affichés TTC. {DELIVERY_DELAY}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {OFFERS.map((o, i) => (
              <OfferCard key={o.id} offer={o} delay={i * 0.08} />
            ))}
          </div>
        </div>
      </section>

      {/* ——— AVIS (structure prête, vide) ——— */}
      <section className="bg-white py-20 sm:py-28">
        <div className="container-nv">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow">Avis clients</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-5xl">
              Ils nous confieront leur image.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              className="mx-auto mt-12 max-w-3xl rounded-3xl border-2 border-dashed border-nuit-900/15 bg-brume p-10 text-center sm:p-14"
              data-testid="reviews-placeholder"
            >
              <Star className="mx-auto h-8 w-8 text-electric" />
              <p className="mt-4 font-display text-xl font-semibold text-nuit-900">
                Les premiers avis clients arrivent bientôt.
              </p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-steel">
                Noveocard est une marque jeune : plutôt que d'inventer des témoignages, nous
                préférons laisser la place à nos premiers clients. Soyez l'un d'eux.
              </p>
              <CtaWhatsApp
                className="mt-7"
                message="Bonjour Noveocard, je souhaite devenir l'un de vos premiers clients."
                testid="reviews-cta-whatsapp"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ——— FAQ COURTE ——— */}
      <section className="bg-brume py-20 sm:py-28">
        <div className="container-nv grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <Reveal>
            <p className="eyebrow">Questions fréquentes</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-4xl">
              L'essentiel, sans détour.
            </h2>
            <Link
              to="/faq"
              data-testid="home-faq-link"
              className="group mt-6 inline-flex items-center gap-2 font-display font-semibold text-electric"
            >
              Consulter la FAQ complète
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible>
              {FAQ_HOME.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`home-${i}`}
                  className="mb-3 rounded-2xl border border-nuit-900/10 bg-white px-6 data-[state=open]:border-electric/40"
                >
                  <AccordionTrigger
                    className="py-5 text-left font-display font-semibold text-nuit-900 hover:no-underline"
                    data-testid={`home-faq-q-${i}`}
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-steel">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ——— CTA FINAL ——— */}
      <section className="grain relative overflow-hidden bg-nuit-900 py-24 text-center text-white sm:py-32">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(50% 60% at 50% 100%, rgba(22,119,255,0.3) 0%, transparent 70%)" }}
          aria-hidden="true"
        />
        <div className="container-nv relative">
          <Reveal>
            <p className="font-display text-4xl font-bold tracking-tight sm:text-6xl">
              Votre identité. En version digitale.
            </p>
            <p className="mx-auto mt-5 max-w-xl text-white/70 sm:text-lg">
              {PRICE_TEASER}. Commandez en quelques minutes sur WhatsApp, nous nous occupons du
              reste.
            </p>
            <div className="mt-9 flex flex-col items-center gap-4">
              <CtaWhatsApp size="lg" showResponseTime testid="final-cta-whatsapp" />
              <Link
                to="/personnalisation"
                data-testid="final-configurator-link"
                className="link-line font-display text-sm font-semibold text-electric-300"
              >
                Ou configurez votre carte en ligne →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
